<script lang="ts">
	import { format } from 'svelte-i18n';
	import { createEventDispatcher, onDestroy } from 'svelte';
	import { load_image_from, real_id } from 'src/helpers/interface_helpers';
	import { createThumbnailResolver } from '../helpers/encryption/recording-image-decryption';
	import { drawerState, edit, getDeviceState, offlineStateType, rooms } from 'src/stores';
	import CameraWidgetCard from './CameraWidgetCard.svelte';
	import DeviceWidget from '../../Lists/Widgets/DeviceWidget.svelte';
	import DeviceMainSlot from 'src/components/Lists/Devices/DeviceMainSlot.svelte';
	import { WidgetCard, Label } from '@Shelly/shelly-design-system/index';

	export let device: any;
	export let widgetCardRef: HTMLElement;
	export let description: any;
	export let minHeight: number | undefined;
	const dispatch = createEventDispatcher();

	$: deviceId = real_id(device?.id);
	$: deviceStateStore = getDeviceState(device?.id);
	$: deviceState = $deviceStateStore;
	$: isOffline = deviceState?.type === offlineStateType;
	$: isLoading = deviceState?.isLoading;

	$: deviceImage = device?.image || device?._meta?.image;

	let cameraPreviewImage = '';

	const previewResolver = createThumbnailResolver((url) => (cameraPreviewImage = url));

	$: previewResolver.update({ deviceId, previewSize: 'large' });
	$: hasCameraPreview = !!cameraPreviewImage;

	onDestroy(previewResolver.destroy);

	// Use camera preview as primary image
	$: imagePoster = hasCameraPreview ? cameraPreviewImage : '';

	// Determine if we should use WidgetCard (offline) or CameraWidgetCard (online)
	// Use WidgetCard when the device is offline
	// Use CameraWidgetCard when the device is online
	$: useWidgetCard = isOffline && !hasCameraPreview;

	// Generate avatar the same way as DashListHolder does (lines 723-746)
	$: deviceServer = device?.localImage ? '' : device?.server;
	$: avatarImage = load_image_from(deviceImage, deviceServer);

	$: hasCriticalNotifications = device?.criticalNotifications?.length > 0;
	$: avatarData = {
		image: avatarImage,
		backgroundColor: device?.backgroundColor || device?._meta?.backgroundColor,
		icon: device?.icon || 'fas fa-video',
		disabled: isOffline,
		notification: hasCriticalNotifications ? { type: 'danger' as const } : null,
	};

	$: deviceName = device?.name || device?._meta?.name || '';
	$: roomName = device?.room_id !== undefined ? $rooms[device.room_id]?.name : '';

	function handleWidgetClick() {
		dispatch('childWidgetClick');
	}
</script>

{#if useWidgetCard}
	<!-- Use default widget card -->
	<WidgetCard
		bind:widgetCardRef
		{minHeight}
		editMode={$edit}
		selected={device?.id === $drawerState.id}
		title={deviceName}
		subtitle={{ value: roomName }}
		hideSlotMain={isOffline}
		{description}
		avatar={avatarData}
		on:click={handleWidgetClick}
	>
		<Label
			slot="leftBadge"
			icon="fas fa-microchip"
			value={$format('label_device', { default: 'Device' })}
			type="default"
			size="s"
			noRadiusType="second"
		/>
		<svelte:fragment slot="main">
			<DeviceMainSlot {device} />
		</svelte:fragment>

		<svelte:fragment slot="bottom">
			{#if !$edit}
				<DeviceWidget {device} />
			{:else}
				<slot name="bottom" />
			{/if}
		</svelte:fragment>
	</WidgetCard>
{:else}
	<!-- Use camera widget card -->
	<CameraWidgetCard
		bind:widgetCardRef
		{minHeight}
		editMode={$edit}
		title={deviceName}
		subtitle={{ value: roomName }}
		{description}
		image={imagePoster}
		disabled={isOffline}
		{isLoading}
		selected={device?.id === $drawerState.id}
		hideSlotBottom={!$edit}
		hideSlotMain={isOffline}
		leftBadge={{
			icon: 'fas fa-microchip',
			value: $format('label_device', { default: 'Device' }),
			type: 'default',
			size: 's',
		}}
		on:click={handleWidgetClick}
	>
		<svelte:fragment slot="main">
			{#if !deviceState?.hideSlotMain}
				<DeviceMainSlot {device} />
			{/if}
		</svelte:fragment>

		<svelte:fragment slot="bottom">
			<slot name="bottom" />
		</svelte:fragment>
	</CameraWidgetCard>
{/if}
